import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ControlledModal from "../Modal/ControlledModal";
import { Button } from "../ui/button";
import { TOKEN } from "../../helper/constant";
import { useTypedSelector } from "../../reactWrapper/Redux/utilities";
import {
  usePreviewTalentImportMutation,
  useCreateTalentImportMutation,
  useLazyGetTalentImportQuery,
  useLazyGetTalentImportRowsQuery,
  useRetryTalentImportMutation,
} from "../../reactWrapper/Redux/apiSlice";

/**
 * Register talents from a spreadsheet.
 *
 * Three steps, in order, and the middle one is not optional: pick a file and
 * it is read and judged without writing anything, you see exactly who would
 * be created and who would be skipped, and only then does Register do it.
 * Unlike a taxonomy import this one sends email, and an email cannot be
 * taken back — so the preview is the safety, not a convenience.
 *
 * The work itself happens on the server a few rows at a time, so the page
 * polls. Closing it does not stop anything.
 */

const API = process.env.REACT_APP_BASE_API_URL;

const DEFAULT_CTA = "Set your password";

const DEFAULT_SUBJECT = "Your Droomwork account is ready";
const DEFAULT_MESSAGE = `Hi {{firstName}},

We have set up a Droomwork account for you so you can be matched with roles that fit your experience.

Set your password using the button below, then complete your profile so we can start matching you.`;

/** How a preview row reads: what will happen, and in what colour. */
function verdictStyle(verdict) {
  if (verdict === "new") {
    return { label: "Will be created", className: "text-emerald-700" };
  }
  if (verdict === "duplicate") {
    return { label: "Skipped", className: "text-amber-700" };
  }
  return { label: "Skipped", className: "text-red-700" };
}

/** How a finished row reads: what actually happened to this person. */
function rowStatusLabel(status) {
  if (status === "pending") {
    return "Waiting";
  }
  if (status === "created") {
    return "Account made, sending…";
  }
  if (status === "emailed") {
    return "Invited";
  }
  if (status === "send_failed") {
    return "Email failed";
  }
  if (status === "bounced") {
    return "Bounced";
  }
  if (status === "duplicate") {
    return "Skipped — already registered";
  }
  if (status === "invalid") {
    return "Skipped — invalid";
  }
  return status;
}

/**
 * The readable part of a failed request. The API puts its own message in
 * data.error.message; the rest are what RTK Query gives us when it never
 * got that far.
 */
function describeError(err) {
  if (err && err.data && err.data.error && err.data.error.message) {
    return err.data.error.message;
  }
  if (err && err.data && err.data.message) {
    return err.data.message;
  }
  if (err && err.error) {
    return err.error;
  }
  return "Something went wrong";
}

/** How many rows of a preview will be skipped rather than registered. */
function skippedCount(summary) {
  if (!summary) {
    return 0;
  }
  return summary.duplicate + summary.invalid;
}

const BulkRegisterTalentsModal = ({ open, onClose, onFinished }) => {
  const [file, setFile] = useState(null);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [ctaLabel, setCtaLabel] = useState(DEFAULT_CTA);
  const [preview, setPreview] = useState(null);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const accessToken = useTypedSelector((s) => s.auth?.accessToken);

  const [runPreviewReq, { isLoading: previewing }] =
    usePreviewTalentImportMutation();
  const [createImport, { isLoading: creating }] =
    useCreateTalentImportMutation();
  const [fetchJob] = useLazyGetTalentImportQuery();
  const [fetchRows] = useLazyGetTalentImportRowsQuery();
  const [retry, { isLoading: retrying }] = useRetryTalentImportMutation();

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => stopPolling, []);

  useEffect(() => {
    if (!open) return;
    stopPolling();
    setFile(null);
    setSubject(DEFAULT_SUBJECT);
    setMessage(DEFAULT_MESSAGE);
    setCtaLabel(DEFAULT_CTA);
    setPreview(null);
    setOnlyProblems(false);
    setConfirming(false);
    setJob(null);
    setRows(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  }, [open]);

  const downloadTemplate = async () => {
    try {
      const token = accessToken || localStorage.getItem(TOKEN) || "";
      const res = await fetch(`${API}/api/v1/admin/talent-imports/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Template download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "talent-import-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  /* Picking a file previews it straight away. There is nothing to decide
     between choosing the sheet and seeing what is in it. */
  const onPick = async (e) => {
    const picked = e.target.files?.[0] || null;
    setFile(picked);
    setPreview(null);
    setError(null);
    if (!picked) return;

    try {
      const out = await runPreviewReq({ file: picked }).unwrap();
      setPreview(out.data);
    } catch (err) {
      setError(describeError(err));
    }
  };

  /**
   * After a run, load only the rows that need a person to look at them.
   *
   * The endpoint is paged, so asking for everything on a 682-row import
   * would quietly show the first page and look complete. The rows worth
   * reading are the ones that did not simply work, and the counts above
   * already say how many did.
   */
  const loadRows = async (importId) => {
    const status = "send_failed,duplicate,invalid";

    try {
      const out = await fetchRows({
        id: importId,
        status,
        perPage: 200,
      }).unwrap();

      // Adonis paginate() answers { total, page, lastPage, data: [...] }.
      let loaded = [];

      if (out.data && out.data.data) {
        loaded = out.data.data;
      }

      setRows(loaded);
    } catch {
      /* The progress line is the important part; the breakdown can wait for
         the next poll rather than throwing an error over a finished run. */
    }
  };

  const follow = (importId) => {
    stopPolling();
    const tick = async () => {
      try {
        const out = await fetchJob(importId).unwrap();
        setJob(out.data);
        if (out.data.status === "done" || out.data.status === "failed") {
          stopPolling();
          await loadRows(importId);
          if (out.data.status === "done") {
            toast.success(
              `${out.data.emailed_count} invited, ${out.data.skipped_count} skipped, ${out.data.failed_count} failed`,
            );
            onFinished?.();
          } else {
            setError(out.data.error || "The import failed");
          }
        }
      } catch {
        /* A dropped poll is not a failed import. The next tick catches up. */
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
  };

  const doImport = async () => {
    setError(null);
    try {
      const out = await createImport({
        file,
        subject: subject.trim(),
        message: message.trim(),
        ctaLabel: ctaLabel.trim(),
      }).unwrap();
      setConfirming(false);
      setJob({
        id: out.data.import_id,
        status: out.data.status,
        total_rows: preview && preview.summary ? preview.summary.total : 0,
        created_count: 0,
        emailed_count: 0,
        failed_count: 0,
        skipped_count: skippedCount(preview ? preview.summary : null),
      });
      follow(out.data.import_id);
    } catch (err) {
      setConfirming(false);
      /* Somebody else is already importing. Follow theirs rather than
         telling this admin to try again into a wall. */
      if (
        err?.data?.error?.code === "IMPORT_BUSY" &&
        err?.data?.error?.import_id
      ) {
        toast.info("Another import is already running. Showing its progress.");
        follow(err.data.error.import_id);
        return;
      }
      setError(describeError(err));
    }
  };

  const doRetry = async () => {
    if (!job?.id) return;
    try {
      const out = await retry(job.id).unwrap();
      toast.success(out.message);

      if (out.data && out.data.requeued > 0) {
        follow(job.id);
      } else {
        await loadRows(job.id);
      }
    } catch (err) {
      toast.error(describeError(err));
    }
  };

  // The preview rows, optionally narrowed to the ones that will be skipped.
  let visibleRows = [];

  if (preview && preview.rows) {
    if (onlyProblems) {
      for (let i = 0; i < preview.rows.length; i++) {
        if (preview.rows[i].verdict !== "new") {
          visibleRows.push(preview.rows[i]);
        }
      }
    } else {
      visibleRows = preview.rows;
    }
  }

  const running = job && job.status !== "done" && job.status !== "failed";
  let summary = null;

  if (preview) {
    summary = preview.summary;
  }

  // How many accounts this sheet would create.
  let toCreate = 0;

  if (summary && summary.create) {
    toCreate = summary.create;
  } else if (job && job.total_rows) {
    toCreate = job.total_rows;
  }

  // A sheet is ready to register when it has somebody to register, the
  // message is written, and nothing else is in flight.
  let canImport = true;

  if (!file || !summary || summary.create < 1) {
    canImport = false;
  } else if (subject.trim() === "" || message.trim() === "") {
    canImport = false;
  } else if (previewing || creating || job) {
    canImport = false;
  }

  return (
    <ControlledModal
      open={open}
      closeModal={() => !creating && onClose?.()}
      closeOnDocumentClick={!creating}
      contentStyle={{
        width: "min(1100px, 97vw)",
        borderRadius: "12px",
        padding: 0,
        border: "none",
        background: "transparent",
      }}>
      {/* `relative` so the confirm step below can cover this panel and
          nothing else — without it, inset-0 escapes to whatever the popup
          happens to position against. */}
      <div className="relative flex max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-card">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-bold text-brand-navy">
            Register talents from a sheet
          </h2>
          <button
            type="button"
            onClick={() => onClose?.()}
            disabled={creating}
            className="text-sm text-muted-foreground hover:text-foreground">
            Close
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
          <p className="text-sm text-muted-foreground">
            One person per row: first name, last name, email and phone number.
            Only the email is required.{" "}
            <button
              type="button"
              onClick={downloadTemplate}
              className="font-medium text-brand-navy underline underline-offset-2">
              Download the template
            </button>
            . Nothing is created and no email is sent until you confirm.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onPick}
              disabled={previewing || creating || !!job}
              className="text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium"
            />
            {previewing ? (
              <span className="text-sm text-muted-foreground">
                Checking the sheet…
              </span>
            ) : null}
          </div>

          {preview?.warnings?.length ? (
            <p className="rounded-md border border-border bg-surface px-3 py-2 text-xs text-muted-foreground">
              {preview.warnings.join(" ")}
            </p>
          ) : null}

          {/* The message. Shown once a sheet is loaded, because writing it
              before knowing the sheet parses is wasted effort. */}
          {preview ? (
            <div className="flex flex-col gap-3 rounded-md border border-border p-4">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-foreground">Subject</span>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={!!job}
                  maxLength={150}
                  className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-foreground">Message</span>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={!!job}
                  rows={7}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <span className="text-xs text-muted-foreground">
                  {
                    "Everyone on this sheet gets this message. Plain text, blank line between "
                  }
                  {
                    "paragraphs. Use {{firstName}}, {{lastName}} or {{fullName}} — single braces "
                  }
                  will not be replaced. Do not paste a link; the button is added
                  for you.
                </span>
              </label>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-foreground">
                  Button wording
                </span>
                <input
                  type="text"
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                  disabled={!!job}
                  maxLength={80}
                  className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                />
                <span className="text-xs text-muted-foreground">
                  Only the wording changes — the button always opens their
                  set-password page.
                </span>
              </label>
            </div>
          ) : null}

          {running ? (
            <div
              role="status"
              aria-live="polite"
              className="rounded-md border border-brand-navy/20 bg-surface px-3 py-2 text-sm text-brand-navy">
              <span className="font-semibold">Registering…</span>{" "}
              <span>
                {job.emailed_count || 0} of {toCreate} invited so far.
              </span>{" "}
              <span className="text-muted-foreground">
                This runs on the server — you can close this and come back.
              </span>
            </div>
          ) : null}

          {error ? (
            <p
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : null}

          {summary ? (
            <div className="flex flex-wrap items-center gap-4 rounded-md border border-border bg-surface px-4 py-3 text-sm">
              <span>
                <strong>{summary.total}</strong> rows
              </span>
              <span className="text-emerald-700">
                <strong>{summary.create}</strong> will be registered
              </span>
              <span className="text-amber-700">
                <strong>{summary.duplicate}</strong> already registered
              </span>
              <span className="text-red-700">
                <strong>{summary.invalid}</strong> invalid
              </span>
              {summary.duplicate + summary.invalid > 0 ? (
                <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={onlyProblems}
                    onChange={(e) => setOnlyProblems(e.target.checked)}
                  />
                  Show only rows that will be skipped
                </label>
              ) : null}
            </div>
          ) : null}

          {/* After the run, the rows needing attention replace the preview —
              the question changes from "who will be created" to "who did
              not work". The summary above answers how many did. */}
          {rows?.length ? (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="min-w-full border-collapse text-left text-sm">
                <thead className="sticky top-0 z-10 bg-surface text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className={
                        r.status === "send_failed" ? "bg-red-50/70" : ""
                      }>
                      <td className="px-3 py-2 text-muted-foreground">
                        {r.row_number}
                      </td>
                      <td className="px-3 py-2">
                        {[r.first_name, r.last_name]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </td>
                      <td className="px-3 py-2">{r.email}</td>
                      <td className="px-3 py-2">
                        {rowStatusLabel(r.status)}
                        {r.error ? (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {r.error}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : visibleRows.length ? (
            <div
              className={`overflow-x-auto rounded-md border border-border ${
                previewing ? "opacity-60" : ""
              }`}>
              <table className="min-w-full border-collapse text-left text-sm">
                <thead className="sticky top-0 z-10 bg-surface text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">First name</th>
                    <th className="px-3 py-2">Last name</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Phone</th>
                    <th className="px-3 py-2">What happens</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => {
                    const v = verdictStyle(r.verdict);
                    return (
                      <tr
                        key={r.row}
                        className={
                          r.verdict === "invalid" ? "bg-red-50/70" : ""
                        }>
                        <td className="px-3 py-2 text-muted-foreground">
                          {r.row}
                        </td>
                        <td className="px-3 py-2">{r.firstName || "—"}</td>
                        <td className="px-3 py-2">{r.lastName || "—"}</td>
                        <td className="px-3 py-2">{r.email || "—"}</td>
                        <td className="px-3 py-2">{r.phone || "—"}</td>
                        <td className={`px-3 py-2 ${v.className}`}>
                          {v.label}
                          {r.reason ? (
                            <div className="mt-1 text-xs text-muted-foreground">
                              {r.reason}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border px-6 py-4">
          <p className="text-xs text-muted-foreground">
            {job
              ? "Listed above are only the rows that need attention. Everyone else was invited."
              : "Rows that would be skipped are listed above. Everything else is registered."}
          </p>

          <div className="flex items-center gap-2">
            {job && !running && job.failed_count > 0 ? (
              <Button
                type="button"
                onClick={doRetry}
                disabled={retrying}
                className="bg-brand-navy text-white hover:bg-brand-navy/90">
                {retrying ? "Retrying…" : `Retry ${job.failed_count} failed`}
              </Button>
            ) : null}

            {!job ? (
              <Button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={!canImport}
                className="bg-brand-navy text-white hover:bg-brand-navy/90">
                {creating ? "Starting…" : `Register ${toCreate} talents`}
              </Button>
            ) : null}
          </div>
        </div>

        {/* The last thing before anything leaves the building. It says the
            number out loud because this is the step that cannot be undone. */}
        {confirming ? (
          <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/40 p-6">
            <div
              role="alertdialog"
              className="w-full max-w-md rounded-xl bg-card p-5 shadow-lg">
              <h3 className="text-base font-bold text-brand-navy">
                Register and send?
              </h3>
              <p className="mt-2 text-sm text-foreground">
                This creates <strong>{toCreate}</strong> accounts and sends{" "}
                <strong>{toCreate}</strong> emails. Emails cannot be taken back.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {skippedCount(summary) > 0
                  ? `${skippedCount(summary)} rows will be skipped.`
                  : "No rows will be skipped."}
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="rounded-md border border-border px-3 py-2 text-sm">
                  Go back
                </button>
                <Button
                  type="button"
                  onClick={doImport}
                  disabled={creating}
                  className="bg-brand-navy text-white hover:bg-brand-navy/90">
                  {creating ? "Starting…" : "Yes, register them"}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </ControlledModal>
  );
};

export default BulkRegisterTalentsModal;
